// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Edits the identity and workspace connections for one new agent.
 *
 * The creation page owns validation and persistence; this form keeps label,
 * stable ID, pipeline, and dashboard fields in one controlled presentation so
 * changing visual layout cannot create a second agent-configuration contract.
 */
import { Bot, Globe2, LayoutGrid, Link2, Workflow } from 'lucide-react'

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/ui'
import { cn } from '@/lib/utils'

interface Option {
  id: string
  label: string
}

interface AgentConfigurationFormProps {
  label: string
  agentId: string
  description: string
  pipelineId: string
  dashboardId: string
  agentIdError: string | null
  workspaceId: string
  pipelines: Option[]
  dashboards: Option[]
  agentIdSynced: boolean
  onLabelChange: (value: string) => void
  onAgentIdChange: (value: string) => void
  onDescriptionChange: (value: string) => void
  onPipelineChange: (value: string) => void
  onDashboardChange: (value: string) => void
}

const initials = (label: string) => label.trim().split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase()

function ConnectionCard({
  icon: Icon,
  title,
  subtitle,
  value,
  options,
  onChange,
}: {
  icon: typeof Workflow
  title: string
  subtitle: string
  value: string
  options: Option[]
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-[13px] rounded-[11px] border border-[#e2e8f0] p-4 transition-[border-color,box-shadow] duration-150 hover:border-[#cbd5e1] hover:shadow-[0_2px_8px_rgba(15,23,42,.04)]">
      <div className="flex items-center gap-[11px]">
        <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[9px] bg-[#f1f5f9] text-[#475569]">
          <Icon className="size-[17px]" />
        </span>
        <span className="flex min-w-0 flex-col gap-px">
          <span className="text-sm font-semibold text-[#0f172a]">{title}</span>
          <span className="text-[12.5px] text-[#94a3b8]">{subtitle}</span>
        </span>
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="!h-[42px] rounded-[9px] border-[#d1d5db] px-3.5 py-0 text-[14.5px] shadow-[0_1px_2px_rgba(0,0,0,.04)] focus-visible:!border-[#93c5fd] focus-visible:!ring-[rgba(37,99,235,.08)]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}

export function AgentConfigurationForm(props: AgentConfigurationFormProps) {
  const badge = initials(props.label)
  return (
    <div data-testid="agent-configuration-panel" className="mt-7 flex flex-col gap-[22px] rounded-[14px] border border-[#e2e8f0] bg-white px-7 pb-[30px] pt-[26px] shadow-[0_1px_3px_rgba(0,0,0,.06)]">
      <div className="text-xs font-semibold uppercase tracking-[.09em] text-[#94a3b8]">Identity</div>

      <div className="flex items-end gap-4">
        <span className="flex size-[52px] shrink-0 items-center justify-center rounded-[13px] border border-[#c7d7f5] bg-gradient-to-br from-[#dbeafe] to-[#e0e7ff] text-[17px] font-bold tracking-[.01em] text-[#2563eb]">
          {badge ? <span className="docreview-pop">{badge}</span> : <Bot className="size-6" />}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <label htmlFor="create-agent-label" className="text-sm font-semibold text-[#0f172a]">Label</label>
          <Input id="create-agent-label" value={props.label} onChange={event => props.onLabelChange(event.target.value)} placeholder="My Agent" autoFocus className="h-11 rounded-[9px] border-[#d1d5db] px-3.5 !text-[15px] shadow-[0_1px_2px_rgba(0,0,0,.04)] focus-visible:!border-[#93c5fd] focus-visible:!ring-[rgba(37,99,235,.08)]" />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2.5">
          <label htmlFor="create-agent-id" className="text-sm font-semibold text-[#0f172a]">Agent ID</label>
          {props.agentIdSynced && (
            <span className="docreview-pop inline-flex h-[22px] items-center gap-1 rounded-full border border-[#dbeafe] bg-[#eff6ff] px-2.5 text-[11.5px] font-semibold text-[#2563eb]">
              <Link2 className="size-[11px]" />Synced with label
            </span>
          )}
        </div>
        <Input id="create-agent-id" value={props.agentId} onChange={event => props.onAgentIdChange(event.target.value)} placeholder="my-agent" className={cn('h-11 rounded-[9px] border-[#d1d5db] px-3.5 font-mono !text-[15px] shadow-[0_1px_2px_rgba(0,0,0,.04)] focus-visible:!border-[#93c5fd] focus-visible:!ring-[rgba(37,99,235,.08)]', props.agentIdError && 'border-destructive')} />
        {props.agentIdError ? <p className="text-xs text-destructive">{props.agentIdError}</p> : (
          <div className="flex items-center gap-2 overflow-hidden whitespace-nowrap font-mono text-[12.5px] leading-[1.5] text-[#94a3b8]">
            <span className="flex shrink-0 items-center gap-1 text-[#64748b]"><Globe2 className="size-3" />URL</span>
            <span className="overflow-hidden text-ellipsis">{props.workspaceId}/agents/<span className="font-semibold text-[#2563eb]">{props.agentId || 'my-agent'}</span></span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="create-agent-description" className="text-sm font-semibold text-[#0f172a]">Description</label>
        <Textarea id="create-agent-description" value={props.description} onChange={event => props.onDescriptionChange(event.target.value)} placeholder="What this agent does" rows={3} className="min-h-[92px] resize-y rounded-[9px] border-[#d1d5db] px-3.5 py-3 !text-[15px] leading-[1.5] shadow-[0_1px_2px_rgba(0,0,0,.04)] focus-visible:!border-[#93c5fd] focus-visible:!ring-[rgba(37,99,235,.08)]" />
      </div>

      <div className="mx-[-28px] my-1.5 h-px bg-[#eef2f6]" />

      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold uppercase tracking-[.09em] text-[#94a3b8]">Connections</div>
        <div className="text-[13.5px] leading-[1.5] text-[#64748b]">Optional — wire this agent into an existing pipeline and dashboard. You can change these later.</div>
      </div>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <ConnectionCard icon={Workflow} title="Pipeline" subtitle="Runs when documents arrive" value={props.pipelineId} options={props.pipelines} onChange={props.onPipelineChange} />
        <ConnectionCard icon={LayoutGrid} title="Dashboard" subtitle="Where results are reviewed" value={props.dashboardId} options={props.dashboards} onChange={props.onDashboardChange} />
      </div>
    </div>
  )
}
